/**
 * Utility functions for handling i18n in configuration files
 */

/**
 * Processes a configuration object to resolve i18n keys
 * @param {Object} config - Configuration object that may contain i18n keys
 * @param {Function} t - Translation function from useTranslations
 * @param {Object} translationData - Additional translation data to use for resolving keys
 * @returns {Object} - Configuration object with resolved translations
 */
export function resolveI18nConfig(config, t, translationData = {}) {
  if (!config || !t) return config;

  const resolvedConfig = { ...config };

  // Resolve title if it's an i18n key
  if (
    typeof resolvedConfig.title === "string" &&
    isI18nKey(resolvedConfig.title)
  ) {
    try {
      resolvedConfig.title = t(resolvedConfig.title, translationData);
    } catch (error) {
      console.warn(
        `Failed to resolve i18n key: ${resolvedConfig.title}`,
        error,
      );
    }
  }

  // Resolve description if it's an i18n key
  if (
    typeof resolvedConfig.description === "string" &&
    isI18nKey(resolvedConfig.description)
  ) {
    try {
      resolvedConfig.description = t(
        resolvedConfig.description,
        translationData,
      );
    } catch (error) {
      console.warn(
        `Failed to resolve i18n key: ${resolvedConfig.description}`,
        error,
      );
    }
  }

  // Resolve link.text if it's an i18n key
  if (
    resolvedConfig.link &&
    typeof resolvedConfig.link.text === "string" &&
    isI18nKey(resolvedConfig.link.text)
  ) {
    try {
      resolvedConfig.link = {
        ...resolvedConfig.link,
        text: t(resolvedConfig.link.text, translationData),
      };
    } catch (error) {
      console.warn(
        `Failed to resolve i18n key: ${resolvedConfig.link.text}`,
        error,
      );
    }
  }

  // Resolve tab labels if they are i18n keys
  if (resolvedConfig.settings?.tab?.tabs) {
    resolvedConfig.settings = {
      ...resolvedConfig.settings,
      tab: {
        ...resolvedConfig.settings.tab,
        tabs: resolvedConfig.settings.tab.tabs.map((tab) => {
          if (typeof tab.label === "string" && isI18nKey(tab.label)) {
            try {
              return {
                ...tab,
                label: t(tab.label, translationData),
              };
            } catch (error) {
              console.warn(`Failed to resolve i18n key: ${tab.label}`, error);
              return tab;
            }
          }
          return tab;
        }),
      },
    };
  }

  return resolvedConfig;
}

/**
 * Checks if a string is an i18n key (contains dots and doesn't start with http)
 * @param {string} str - String to check
 * @returns {boolean} - True if string appears to be an i18n key
 */
export function isI18nKey(str) {
  return (
    typeof str === "string" && str.includes(".") && !str.startsWith("http")
  );
}

/**
 * Creates a flexible i18n configuration for section components
 * @param {Object} baseConfig - Base configuration object
 * @param {Object} i18nKeys - Object containing i18n keys for title, description, link, etc.
 * @returns {Object} - Configuration object with i18n keys
 */
export function createI18nConfig(baseConfig, i18nKeys = {}) {
  return {
    ...baseConfig,
    title: i18nKeys.title || baseConfig.title,
    description: i18nKeys.description || baseConfig.description,
    link: i18nKeys.link
      ? {
          ...baseConfig.link,
          text: i18nKeys.link.text || baseConfig.link?.text,
        }
      : baseConfig.link,
    // Add support for other i18n fields as needed
    ...(i18nKeys.other && { other: i18nKeys.other }),
  };
}
